import React, { useContext, useEffect, useState } from 'react'
import { Dialog } from '@/components/ui/dialog'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridColumnProps,
} from '@progress/kendo-react-grid'
import { MarketingCampaignDetailDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { LuckyDrawRewardsDto } from 'api/Trading-Accounts/Types/MarketingCampaignResultsDto'
import { isMarketingCampaignLuckyDraw } from 'api/Trading-Accounts/Types/MarketingCampaignType'
import AddButton from 'components/AddButton/AddButton'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useGridState, useGridStatusState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchLuckyDrawRewardTypesAction } from 'store/marketing/marketing.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { isEmpty } from 'utils/is-empty'
import { TableWithExport } from 'utils/table/withExport'

import { AddLuckyDrawRewardForm, RewardDetailsFormValues } from './AddLuckyDrawRewardForm'
import { CampaignTab } from './CampaignSlug'
import { CampaignNavigation } from './Navigation/CampaignNavigation'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

interface OuterProps {
  data: MarketingCampaignDetailDto
  columns: GridColumn[]
}

export const CampaignDetailsRewardsPage: React.FC<OuterProps> = ({ data, ...props }) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const dispatch = useAppDispatch()
  const luckyDrawRewardTypes = useAppSelector((state) => state.marketing.luckyDrawRewardTypes)

  const setToast = useContext(ToastContext)
  const [campaignRewards, setCampaignRewards] = useState<LuckyDrawRewardsDto[]>([])
  const [addRewardModal, setAddRewardModal] = useState(false)
  const [deleteRewardId, setDeleteRewardId] = useState<string>()
  const [selectedReward, setSelectedReward] = useState<LuckyDrawRewardsDto | undefined>()
  const [confirmTopPrize, setConfirmTopPrize] = useState<{
    open: boolean
    resolve?: (allow: boolean) => void
  }>({ open: false })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState } = useGridState()

  const updateRewards = async () => {
    await fetchGrid(async (): Promise<void> => {
      const response = await tradingAccountsApiClient.getMarketingCampaignRewards(data.id)
      await setCampaignRewards(response as LuckyDrawRewardsDto[])
    })
  }

  useEffect(() => {
    updateRewards()
    if (isEmpty(luckyDrawRewardTypes)) {
      dispatch(fetchLuckyDrawRewardTypesAction(tradingAccountsApiClient))
    }
  }, [dispatch])

  const fetchGrid = async (callback: () => Promise<void>) => {
    gridStatusActions.setLoading(true)
    await callback()
    gridStatusActions.setLoading(false)
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  const handleSubmit = async (values: RewardDetailsFormValues) => {
    try {
      if (values?.id) {
        await tradingAccountsApiClient.updateLuckyDrawReward(data.id, values)
      } else {
        await tradingAccountsApiClient.addLuckyDrawReward(data.id, values)
      }
      updateRewards()
      setToast(successToast(`Lucky draw reward ${values.id ? 'edited' : 'added'} successfully.`))
    } finally {
      handleCloseAddRewardModal()
    }
  }

  const handleDeleteReward = async () => {
    try {
      if (deleteRewardId) {
        await tradingAccountsApiClient.deleteLuckyDrawReward(data.id, deleteRewardId)
      }
      await updateRewards()
      setToast(successToast(`Lucky draw reward deleted successfully.`))
    } catch (e) {
      console.log({ e })
    } finally {
      setDeleteRewardId(undefined)
    }
  }

  const handleCloseAddRewardModal = () => {
    setAddRewardModal(false)
    setSelectedReward(undefined)
  }

  const requestTopPrizeChange = (_nextValue: boolean): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmTopPrize({ open: true, resolve })
    })
  }

  return (
    <>
      {addRewardModal && (
        <Dialog open={addRewardModal} onOpenChange={(open) => !open && handleCloseAddRewardModal()}>
          <AddLuckyDrawRewardForm
            onSubmit={handleSubmit}
            selectedReward={selectedReward}
            currencies={data.currencies.map((x) => x.currencyId)}
            defaultCurrency={data.defaultCurrency.id}
            rewardTypes={luckyDrawRewardTypes}
            availableNumbers={Array.from({ length: 24 }, (_, i) => i + 1).filter(
              (n) => !campaignRewards.some((r) => Number(r.number) === n)
            )}
            onTopPrizeChangeRequest={requestTopPrizeChange}
          />
        </Dialog>
      )}

      <ConfirmationModal
        renderTitle={'Delete Reward'}
        contentText='Are you sure you want to delete this reward?'
        yesButtonText='Delete'
        cancelButtonText='Cancel'
        onYes={handleDeleteReward}
        onCancel={() => setDeleteRewardId(undefined)}
        visible={!!deleteRewardId}
      />

      <ConfirmationModal
        contentText='Are you sure you want to change the top price?'
        yesButtonText='Yes'
        onYes={() => {
          confirmTopPrize.resolve?.(true)
          setConfirmTopPrize({ open: false })
        }}
        onCancel={() => {
          confirmTopPrize.resolve?.(false)
          setConfirmTopPrize({ open: false })
        }}
        visible={confirmTopPrize.open}
      />

      <CampaignNavigation data={data} tab={CampaignTab.REWARDS} />
      <AddButton text='Add Reward' onClick={() => setAddRewardModal(true)} />

      <TableWithExport data={campaignRewards} columns={props.columns}>
        <Grid {...gridState} resizable data={campaignRewards} style={{ zIndex: 0 }}>
          {props.columns.map((column, idx) => (
            <Column
              key={idx + 2}
              field={column.field}
              title={column.title}
              cell={column.cell}
              className={column.className}
              width={column.width}
              minResizableWidth={column.minResizableWidth}
            />
          ))}

          <Column
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      onClick: () => {
                        setAddRewardModal(true)
                        setSelectedReward(gridCellProps.dataItem)
                      },
                    },
                    {
                      name: 'Delete',
                      disabled: !isMarketingCampaignLuckyDraw(data.type.id),
                      onClick: () => {
                        setDeleteRewardId(gridCellProps.dataItem.id)
                      },
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </TableWithExport>
    </>
  )
}
